import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { IArtistsRosterProps, IArtistRosterCard } from '../types';

const formatListeners = (val: string) => {
    // Keeps provided string like "34.5M"; for numbers, formats to K/M.
    if (/[a-zA-Z]/.test(val)) return val;
    const n = Number(val);
    if (Number.isNaN(n)) return val;
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return Math.round(n / 1_000) + 'K';
    return String(n);
};

const ArtistCard = ({ artist }: { artist: IArtistRosterCard }) => {
    const navigate = useNavigate();
    const isUp = artist.change >= 0;
    const trendColor = isUp ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400';
    const trendIcon = isUp ? 'ph-arrow-up' : 'ph-arrow-down';
    const openProfile = () => {
        if (artist.id) navigate(`/artists/${artist.id}`);
    };

    return (
        <button
            type="button"
            onClick={openProfile}
            className="group relative bg-subtle rounded-xl overflow-hidden border border-default shadow-sm hover:shadow-md transition-all duration-200 h-full text-left focus:outline-none focus:ring-2 focus:ring-[#1DB954]"
            aria-label={`Abrir perfil de ${artist.name}`}
        >
            <img
                src={artist.imgSrc}
                alt={artist.name}
                className="w-full h-28 md:h-32 object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://placehold.co/400x200?text=Artist'; }}
            />

            {/* Overlay gradient */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />

            {/* Trend chip */}
            <div className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${isUp ? 'bg-green-500/15 text-green-600 dark:text-green-300' : 'bg-red-500/15 text-red-600 dark:text-red-300'} border border-white/20 dark:border-zinc-700/50`}
                title={isUp ? 'Crescendo' : 'Em queda'}
            >
                <i className={`ph ${trendIcon}`} /> {Math.abs(artist.change)}%
            </div>

            {/* Content */}
            <div className="p-3 flex flex-col gap-1">
                <p className="font-bold text-zinc-50 drop-shadow-md truncate w-full">
                    {artist.name}
                </p>
                <p className="text-[12px] text-zinc-200/90 flex items-center gap-1">
                    <i className="ph ph-headphones" /> {formatListeners(artist.listeners)} ouvintes
                </p>
            </div>

            {/* Hover action */}
            <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 text-zinc-900 text-xs font-semibold shadow-sm dark:bg-zinc-800/80 dark:text-zinc-100">
                    <i className="ph ph-user-focus" /> Ver detalhes
                </span>
            </div>
        </button>
    );
};

export const ArtistsRoster = ({ artists }: IArtistsRosterProps) => (
    <div className="bg-surface p-4 rounded-lg stat-card fade-in border border-default" style={{ animationDelay: '0.2s' }}>
        <div className="flex items-center justify-between mb-3">
            <h3 className="text-base xl:text-lg font-bold text-zinc-900 dark:text-white">Artists Roster</h3>
            <a href="#" className="text-sm text-green-500 dark:text-green-400 hover:underline">Gerenciar</a>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {artists.map(artist => (
                <ArtistCard key={artist.id || artist.name} artist={artist} />
            ))}
        </div>
    </div>
);
